import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Collapse, FormControl, InputLabel, MenuItem, Paper, Select, Snackbar, TextField, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { MaterialReactTable } from 'material-react-table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import intercomV2Service from '../../services/intercomV2.service';
import { downloadCSV } from '../../utils/exportUtils';
import { getJwtToken } from '../../services/jwt.services';
import jwt_decode from 'jwt-decode';
import styles from '../Intercom/Intercom.module.css';

const formatExportTimestamp = (date) => {
	const pad = (n) => String(n).padStart(2, '0');
	return (
		`${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
		`-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
	);
};

const DEFAULT_PAGE_SIZE = 20;

const CONVERSATION_STATUS_OPTIONS = [
	{ value: '', label: 'All' },
	{ value: 'open', label: 'Open' },
	{ value: 'closed', label: 'Closed' },
	{ value: 'snoozed', label: 'Snoozed' },
];

const TICKET_OPEN_OPTIONS = [
	{ value: '', label: 'All' },
	{ value: 'true', label: 'Open' },
	{ value: 'false', label: 'Closed' },
];

const TICKET_STATE_OPTIONS = [
	{ value: '', label: 'All' },
	{ value: 'submitted', label: 'Submitted' },
	{ value: 'in_progress', label: 'In progress' },
	{ value: 'waiting_on_customer', label: 'Waiting on customer' },
	{ value: 'resolved', label: 'Resolved' },
];

const TICKETS_ONLY_OPTIONS = [
	{ value: 'false', label: 'No' },
	{ value: 'true', label: 'Yes' },
];

const DATE_PRESET_OPTIONS = [
	{ value: 'any', label: 'Any' },
	{ value: 'last_7_days', label: 'Last 7 days' },
	{ value: 'this_month', label: 'This month' },
	{ value: 'custom', label: 'Custom' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const toDateTimeLocal = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');
	return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const getDefaultDateRange = () => {
	const toDate = new Date();
	const fromDate = new Date(toDate.getTime() - 7 * 24 * 60 * 60 * 1000);
	return {
		from: toDateTimeLocal(fromDate),
		to: toDateTimeLocal(toDate),
	};
};

const getThisMonthDateRange = () => {
	const toDate = new Date();
	const fromDate = new Date(toDate.getFullYear(), toDate.getMonth(), 1, 0, 0, 0, 0);
	return {
		from: toDateTimeLocal(fromDate),
		to: toDateTimeLocal(toDate),
	};
};

const resolveDateRangeForPreset = (datePreset, from, to) => {
	if (datePreset === 'any') {
		return { from: '', to: '' };
	}
	if (datePreset === 'this_month') {
		return getThisMonthDateRange();
	}
	if (datePreset === 'last_7_days') {
		return getDefaultDateRange();
	}
	return { from, to };
};

const hasAnyNonDateFilter = (filters) => {
	return Boolean(
		filters.freeText ||
		filters.email ||
		filters.domain ||
		filters.accountId ||
		filters.ticketsOnly === 'true' ||
		filters.conversationId ||
		filters.ticketId ||
		filters.conversationStatus ||
		filters.ticketOpen ||
		filters.ticketStateCategory
	);
};

const detectLookupType = (rawValue) => {
	const value = (rawValue || '').trim();
	if (!value) {
		return { type: '', value: '' };
	}

	const normalized = value;
	if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
		return { type: 'email', value: normalized.toLowerCase() };
	}
	if (/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(normalized) && !normalized.includes('@')) {
		return { type: 'domain', value: normalized.toLowerCase() };
	}

	if (/^\d{13,18}$/.test(normalized)) {
		return { type: 'conversationId', value: normalized };
	}
	if (/^\d{5,12}$/.test(normalized)) {
		return { type: 'ticketId', value: normalized };
	}

	const accountCandidate = normalized.replace(/^s-\s*/i, '');
	if (
		/^[a-z0-9]{15,18}$/i.test(accountCandidate) &&
		/[a-z]/i.test(accountCandidate)
	) {
		return { type: 'accountId', value: accountCandidate };
	}
	return { type: 'freeText', value: normalized };
};

const toIsoString = (value) => {
	if (!value) {
		return '';
	}
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const formatTimestamp = (timestamp) => {
	if (!timestamp) {
		return '';
	}
	const date = new Date(timestamp);
	return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

const getTicketIds = (conversation) => {
	if (!Array.isArray(conversation?.tickets)) {
		return '';
	}
	return conversation.tickets
		.map((ticket) => ticket?.ticketId || ticket?.intercom_ticket_id)
		.filter(Boolean)
		.join(', ');
};

const IntercomV2Conversations = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const defaultDateRange = useMemo(() => getDefaultDateRange(), []);
	const thisMonthDateRange = useMemo(() => getThisMonthDateRange(), []);

	const getInitialDatePreset = useCallback(() => {
		const rawPreset = searchParams.get('datePreset');
		if (DATE_PRESET_OPTIONS.some((option) => option.value === rawPreset)) {
			return rawPreset;
		}
		if (searchParams.get('from') || searchParams.get('to')) {
			return 'custom';
		}
		return 'last_7_days';
	}, [searchParams]);

	const getInitialFilters = useCallback(() => {
		const datePreset = getInitialDatePreset();
		const paramFrom = searchParams.get('from');
		const paramTo = searchParams.get('to');

		let from = defaultDateRange.from;
		let to = defaultDateRange.to;
		if (datePreset === 'any') {
			from = '';
			to = '';
		}
		if (datePreset === 'this_month') {
			from = thisMonthDateRange.from;
			to = thisMonthDateRange.to;
		}
		if (datePreset === 'custom') {
			from = paramFrom || defaultDateRange.from;
			to = paramTo || defaultDateRange.to;
		}

		return {
			lookup: searchParams.get('lookup') || '',
			freeText: searchParams.get('freeText') || '',
			email: searchParams.get('email') || '',
			domain: searchParams.get('domain') || '',
			accountId: searchParams.get('accountId') || '',
			ticketsOnly: searchParams.get('ticketsOnly') || 'false',
			conversationId: searchParams.get('conversationId') || '',
			conversationStatus: searchParams.get('conversationStatus') || '',
			ticketId: searchParams.get('ticketId') || '',
			ticketOpen: searchParams.get('ticketOpen') || '',
			ticketStateCategory: searchParams.get('ticketStateCategory') || '',
			datePreset,
			from,
			to,
		};
	}, [defaultDateRange.from, defaultDateRange.to, getInitialDatePreset, searchParams, thisMonthDateRange.from, thisMonthDateRange.to]);

	const [filters, setFilters] = useState(() => getInitialFilters());

	const [appliedFilters, setAppliedFilters] = useState(() => getInitialFilters());

	const [page, setPage] = useState(() => {
		const pageParam = Number(searchParams.get('page') || 1);
		return Number.isNaN(pageParam) || pageParam < 1 ? 1 : pageParam;
	});

	const [pageSize, setPageSize] = useState(() => {
		const sizeParam = Number(searchParams.get('size') || DEFAULT_PAGE_SIZE);
		return PAGE_SIZE_OPTIONS.includes(sizeParam) ? sizeParam : DEFAULT_PAGE_SIZE;
	});

	const [conversations, setConversations] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [isExporting, setIsExporting] = useState(false);
	const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
	const [canExport, setCanExport] = useState(false);

	useEffect(() => {
		const token = getJwtToken();
		if (token) {
			const { intercompermissions } = jwt_decode(token);
			setCanExport(Array.isArray(intercompermissions) && intercompermissions.includes('admin'));
		}
	}, []);
	const [showAdvancedFilters, setShowAdvancedFilters] = useState(() =>
		Boolean(
			searchParams.get('email') ||
			searchParams.get('domain') ||
			searchParams.get('accountId') ||
			searchParams.get('freeText') ||
			searchParams.get('conversationId') ||
			searchParams.get('ticketId') ||
			searchParams.get('conversationStatus') ||
			searchParams.get('ticketOpen') ||
			searchParams.get('ticketStateCategory')
		)
	);

	useEffect(() => {
		document.title = 'Intercom v2 Conversations - GSD Support Admin';
		return () => {
			document.title = 'GSD Support Admin';
		};
	}, []);

	const loadConversations = useCallback(async () => {
		setIsLoading(true);
		setErrorMessage('');
		try {
			const response = await intercomV2Service.searchConversations({
				freeText: appliedFilters.freeText,
				email: appliedFilters.email,
				domain: appliedFilters.domain,
				accountId: appliedFilters.accountId,
				ticketsOnly: appliedFilters.ticketsOnly === 'true',
				conversationId: appliedFilters.conversationId,
				conversationStatus: appliedFilters.conversationStatus,
				ticketId: appliedFilters.ticketId,
				ticketOpen:
					appliedFilters.ticketOpen === ''
						? ''
						: appliedFilters.ticketOpen === 'true',
				ticketStateCategory: appliedFilters.ticketStateCategory,
				includeDateRange: appliedFilters.datePreset !== 'any',
				from: toIsoString(appliedFilters.from),
				to: toIsoString(appliedFilters.to),
				page,
				size: pageSize,
			});

			const result = response?.result || {};
			const paging = result?.paging || {};
			setConversations(result?.conversations || []);
			setTotalCount(paging?.conversation_total || 0);
		} catch (error) {
			setErrorMessage(error?.message || 'Failed to load Intercom v2 conversations.');
		} finally {
			setIsLoading(false);
		}
	}, [appliedFilters, page, pageSize]);

	useEffect(() => {
		loadConversations();
	}, [loadConversations]);

	const handleSearch = () => {
		const resolvedDateRange = resolveDateRangeForPreset(filters.datePreset, filters.from, filters.to);
		const lookupResult = detectLookupType(filters.lookup);
		const nextFilters = {
			lookup: filters.lookup.trim(),
			freeText: filters.freeText.trim(),
			email: filters.email.trim(),
			domain: filters.domain.trim(),
			accountId: filters.accountId.trim(),
			ticketsOnly: filters.ticketsOnly,
			conversationId: filters.conversationId.trim(),
			conversationStatus: filters.conversationStatus,
			ticketId: filters.ticketId.trim(),
			ticketOpen: filters.ticketOpen,
			ticketStateCategory: filters.ticketStateCategory,
			datePreset: filters.datePreset,
			from: resolvedDateRange.from,
			to: resolvedDateRange.to,
		};

		if (lookupResult.type) {
			nextFilters.freeText = '';
			nextFilters.email = '';
			nextFilters.domain = '';
			nextFilters.accountId = '';
			nextFilters.conversationId = '';
			nextFilters.ticketId = '';
			nextFilters[lookupResult.type] = lookupResult.value;
		}

		if (!hasAnyNonDateFilter(nextFilters) && nextFilters.datePreset === 'any') {
			const fallbackDateRange = getDefaultDateRange();
			nextFilters.datePreset = 'last_7_days';
			nextFilters.from = fallbackDateRange.from;
			nextFilters.to = fallbackDateRange.to;
		}

		setAppliedFilters(nextFilters);
		setFilters(nextFilters);
		setPage(1);
		setSearchParams({
			...nextFilters,
			page: '1',
			size: String(pageSize),
		});
	};

	const handleReset = () => {
		const defaultRange = getDefaultDateRange();
		const cleared = {
			lookup: '',
			freeText: '',
			email: '',
			domain: '',
			accountId: '',
			ticketsOnly: 'false',
			conversationId: '',
			conversationStatus: '',
			ticketId: '',
			ticketOpen: '',
			ticketStateCategory: '',
			datePreset: 'last_7_days',
			from: defaultRange.from,
			to: defaultRange.to,
		};
		setFilters(cleared);
		setAppliedFilters(cleared);
		setPage(1);
		setPageSize(DEFAULT_PAGE_SIZE);
		setSearchParams({
			...cleared,
			page: '1',
			size: String(DEFAULT_PAGE_SIZE),
		});
	};

	const handleFilterKeyDown = (event) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			handleSearch();
		}
	};

	const handleExport = async () => {
		if (isExporting) return;
		setIsExporting(true);
		try {
			const response = await intercomV2Service.exportConversations({
				freeText: appliedFilters.freeText,
				email: appliedFilters.email,
				domain: appliedFilters.domain,
				accountId: appliedFilters.accountId,
				ticketsOnly: appliedFilters.ticketsOnly === 'true',
				conversationId: appliedFilters.conversationId,
				conversationStatus: appliedFilters.conversationStatus,
				ticketId: appliedFilters.ticketId,
				ticketOpen:
					appliedFilters.ticketOpen === ''
						? ''
						: appliedFilters.ticketOpen === 'true',
				ticketStateCategory: appliedFilters.ticketStateCategory,
				includeDateRange: appliedFilters.datePreset !== 'any',
				from: toIsoString(appliedFilters.from),
				to: toIsoString(appliedFilters.to),
			});

			const filename = `intercom-export-${formatExportTimestamp(new Date())}.csv`;
			downloadCSV(response.csvData, filename);
			setToast({
				open: true,
				severity: 'success',
				message: `Exported ${response.count} conversations`,
			});
		} catch (error) {
			const status = error?.response?.status;
			const apiError = error?.response?.data?.error;
			if (status === 413) {
				setToast({
					open: true,
					severity: 'warning',
					message: apiError || 'Export exceeds maximum row limit; refine your filters and try again.',
				});
			} else {
				setToast({
					open: true,
					severity: 'error',
					message: apiError || error?.message || 'Failed to export conversations.',
				});
			}
		} finally {
			setIsExporting(false);
		}
	};

	const columns = useMemo(
		() => [
			{
				header: 'View',
				id: 'view',
				Cell: ({ row }) => {
					const conversationId = row.original?.conversationId || row.original?.intercom_conversation_id;
					const hasTickets = Array.isArray(row.original?.tickets) && row.original.tickets.length > 0;
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							{hasTickets ? (
								<Tooltip title="View conversation + ticket details">
									<span>
										<Button
											variant="text"
											size="small"
											disabled={!conversationId}
											onClick={() => navigate(`/intercom/conversations/${conversationId}`)}
										>
											<ChatBubbleOutlineIcon fontSize="small" />
											<ConfirmationNumberIcon fontSize="small" />
										</Button>
									</span>
								</Tooltip>
							) : (
								<Tooltip title="Click to see the conversation">
									<span>
										<Button
											variant="text"
											size="small"
											disabled={!conversationId}
											onClick={() => navigate(`/intercom/conversations/${conversationId}`)}
										>
											<ChatBubbleOutlineIcon fontSize="small" />
										</Button>
									</span>
								</Tooltip>
							)}
						</Box>
					);
				},
			},
			{
				header: 'Email',
				accessorFn: (row) => row?.author_email || '',
				id: 'email',
			},
			{
				header: 'Title',
				accessorFn: (row) => row?.title || '',
				id: 'title',
			},

			{
				header: 'Status',
				accessorFn: (row) => row?.status || row?.state || '',
				id: 'status',
			},
			{
				header: 'Created',
				accessorFn: (row) => formatTimestamp(row?.created || row?.created_at),
				id: 'created',
			},
			{
				header: 'Account ID',
				accessorFn: (row) => row?.custom_attributes?.['🪪 Account ID'] || '',
				id: 'accountId',
			},
			{
				header: 'Conversation ID',
				accessorFn: (row) => row?.conversationId || row?.intercom_conversation_id || '',
				id: 'conversationId',
			},
			{
				header: 'Channel',
				accessorFn: (row) => row?.channel || '',
				id: 'channel',
			},
			{
				header: 'Ticket IDs',
				accessorFn: (row) => getTicketIds(row),
				id: 'ticketIds',
			},
			{
				header: 'Updated',
				accessorFn: (row) => formatTimestamp(row?.updated || row?.updated_at),
				id: 'updated',
			},
		],
		[navigate]
	);

	return (
		<div className={styles.divStyle}>
			<div className={styles.sectionContainer}>
				<div className={styles.sectionHeader}>
					<Tooltip title="Home">
						<button type="button" className={styles.backButton} onClick={() => navigate('/')}>
							<ArrowBackIcon className={styles.backIcon} />
						</button>
					</Tooltip>
					<div>
						<h1 className={styles.sectionTitle}>Intercom Conversations - V2</h1>
						<p className={styles.sectionDescription}>
							Search conversations using the new Intercom Search API. Default date range is last 7 days.
						</p>
					</div>
				</div>
			</div>

			<Paper className={styles.tableContainer}>
				<Box className={styles.filtersRow}>
					<TextField
						label="Smart Lookup"
						variant="outlined"
						size="small"
						value={filters.lookup}
						onChange={(event) => setFilters((prev) => ({ ...prev, lookup: event.target.value }))}
						onKeyDown={handleFilterKeyDown}
						placeholder="Email, domain, account id, conversation id, or ticket number"
						className={`${styles.filterField} ${styles.smartLookupField}`}
					/>
					<FormControl size="small" className={styles.filterField}>
						<InputLabel>Tickets Only</InputLabel>
						<Select
							label="Tickets Only"
							value={filters.ticketsOnly}
							onChange={(event) => setFilters((prev) => ({ ...prev, ticketsOnly: event.target.value }))}
						>
							{TICKETS_ONLY_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl size="small" className={styles.filterField}>
						<InputLabel>Date Range</InputLabel>
						<Select
							label="Date Range"
							value={filters.datePreset}
							onChange={(event) => {
								const nextPreset = event.target.value;
								const nextDateRange = resolveDateRangeForPreset(nextPreset, filters.from, filters.to);
								setFilters((prev) => ({
									...prev,
									datePreset: nextPreset,
									from: nextDateRange.from,
									to: nextDateRange.to,
								}));
							}}
						>
							{DATE_PRESET_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					{filters.datePreset === 'custom' && (
						<TextField
							label="Date From"
							type="datetime-local"
							size="small"
							value={filters.from}
							onChange={(event) => setFilters((prev) => ({ ...prev, from: event.target.value }))}
							onKeyDown={handleFilterKeyDown}
							className={styles.filterField}
							InputLabelProps={{ shrink: true }}
						/>
					)}
					{filters.datePreset === 'custom' && (
						<TextField
							label="Date To"
							type="datetime-local"
							size="small"
							value={filters.to}
							onChange={(event) => setFilters((prev) => ({ ...prev, to: event.target.value }))}
							onKeyDown={handleFilterKeyDown}
							className={styles.filterField}
							InputLabelProps={{ shrink: true }}
						/>
					)}
					<Box className={styles.filterActions}>
						<Button variant="text" onClick={() => setShowAdvancedFilters((prev) => !prev)}>
							{showAdvancedFilters ? 'Hide Advanced' : 'Advanced Filters'}
						</Button>
						<Button variant="contained" onClick={handleSearch} disabled={isLoading}>
							Search
						</Button>
						<Button variant="outlined" onClick={handleReset} disabled={isLoading}>
							Reset
						</Button>
						{canExport && (
							<Button
								variant="outlined"
								onClick={handleExport}
								disabled={isLoading || isExporting || totalCount === 0}
								startIcon={isExporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
							>
								{isExporting ? 'Exporting...' : 'Export CSV'}
							</Button>
						)}
					</Box>
				</Box>
				<Collapse in={showAdvancedFilters}>
					<Box className={styles.filtersRow}>
						<TextField
							label="Free Text"
							variant="outlined"
							size="small"
							value={filters.freeText}
							onChange={(event) => setFilters((prev) => ({ ...prev, freeText: event.target.value }))}
							onKeyDown={handleFilterKeyDown}
							placeholder="Keyword or phrase"
							className={styles.filterField}
						/>
						<TextField
							label="Email"
							variant="outlined"
							size="small"
							value={filters.email}
							onChange={(event) => setFilters((prev) => ({ ...prev, email: event.target.value }))}
							onKeyDown={handleFilterKeyDown}
							className={styles.filterField}
						/>
						<TextField
							label="Account ID"
							variant="outlined"
							size="small"
							value={filters.accountId}
							onChange={(event) => setFilters((prev) => ({ ...prev, accountId: event.target.value }))}
							onKeyDown={handleFilterKeyDown}
							className={styles.filterField}
						/>
						<TextField
							label="Conversation ID"
							variant="outlined"
							size="small"
							value={filters.conversationId}
							onChange={(event) => setFilters((prev) => ({ ...prev, conversationId: event.target.value }))}
							onKeyDown={handleFilterKeyDown}
							className={styles.filterField}
						/>
						<TextField
							label="Ticket ID"
							variant="outlined"
							size="small"
							value={filters.ticketId}
							onChange={(event) => setFilters((prev) => ({ ...prev, ticketId: event.target.value }))}
							onKeyDown={handleFilterKeyDown}
							className={styles.filterField}
						/>
						<FormControl size="small" className={styles.filterField}>
							<InputLabel>Conversation Status</InputLabel>
							<Select
								label="Conversation Status"
								value={filters.conversationStatus}
								onChange={(event) => setFilters((prev) => ({ ...prev, conversationStatus: event.target.value }))}
							>
								{CONVERSATION_STATUS_OPTIONS.map((option) => (
									<MenuItem key={option.value || 'all'} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField
							label="Domain"
							variant="outlined"
							size="small"
							value={filters.domain}
							onChange={(event) => setFilters((prev) => ({ ...prev, domain: event.target.value }))}
							onKeyDown={handleFilterKeyDown}
							className={styles.filterField}
						/>
						<FormControl size="small" className={styles.filterField}>
							<InputLabel>Ticket Open</InputLabel>
							<Select
								label="Ticket Open"
								value={filters.ticketOpen}
								onChange={(event) => setFilters((prev) => ({ ...prev, ticketOpen: event.target.value }))}
							>
								{TICKET_OPEN_OPTIONS.map((option) => (
									<MenuItem key={option.value || 'all'} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl size="small" className={styles.filterField}>
							<InputLabel>Ticket State</InputLabel>
							<Select
								label="Ticket State"
								value={filters.ticketStateCategory}
								onChange={(event) => setFilters((prev) => ({ ...prev, ticketStateCategory: event.target.value }))}
							>
								{TICKET_STATE_OPTIONS.map((option) => (
									<MenuItem key={option.value || 'all'} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>
				</Collapse>

				<Box className={styles.metaRow}>
					<Typography variant="body2">
						{totalCount ? `Total conversations: ${totalCount}` : 'No conversations found.'}
					</Typography>
					{errorMessage && (
						<Typography variant="body2" color="error">
							{errorMessage}
						</Typography>
					)}
				</Box>

				<MaterialReactTable
					columns={columns}
					data={isLoading ? [] : conversations}
					enableColumnOrdering
					enableGlobalFilter
					enableColumnFilters={false}
					enableRowSelection={false}
					enableStickyHeader
					manualPagination
					rowCount={totalCount}
					enablePagination
					muiTablePaginationProps={{
						rowsPerPageOptions: PAGE_SIZE_OPTIONS,
					}}
					onPaginationChange={(updater) => {
						const next = typeof updater === 'function'
							? updater({ pageIndex: page - 1, pageSize })
							: updater;
						const nextPage = next.pageIndex + 1;
						const nextSize = next.pageSize;
						setPage(nextPage);
						setPageSize(nextSize);
						setSearchParams({
							...appliedFilters,
							page: String(nextPage),
							size: String(nextSize),
						});
					}}
					state={{
						isLoading,
						pagination: { pageIndex: page - 1, pageSize },
					}}
					initialState={{ density: 'compact', pagination: { pageSize: DEFAULT_PAGE_SIZE } }}
					muiTablePaperProps={{ elevation: 0 }}
					muiTableContainerProps={{ className: styles.tableWrapper }}
				/>
			</Paper>

			<Snackbar
				open={toast.open}
				autoHideDuration={5000}
				onClose={() => setToast((prev) => ({ ...prev, open: false }))}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					severity={toast.severity}
					onClose={() => setToast((prev) => ({ ...prev, open: false }))}
					variant="filled"
				>
					{toast.message}
				</Alert>
			</Snackbar>
		</div>
	);
};

export default IntercomV2Conversations;
